/**
 * Pré-inscription duo (TICKET-PRE28-02) : un copilote est soit COMPLET et
 * VALIDE, soit absent (case « Je ne connais pas encore mon copilote »).
 *
 * Règles partagées par le rendu, la validation et la soumission de la page
 * publique. Le serveur applique les MÊMES règles (migration 00315 : helpers
 * `form_value_is_blank`, `form_birth_date_is_valid`, `form_email_is_valid`,
 * `copilot_submission_missing_fields`, `form_submission_invalid_fields` ;
 * trigger à la soumission + RPC create_payment_case_from_submission, 00316) :
 * toute modification ici doit être reportée côté SQL, et inversement. Les
 * deux suites de tests utilisent les mêmes jeux de valeurs.
 */

import { z } from 'zod';
import type { Form, FormFieldWithValues } from '../types/form';

/** Message levé par le serveur (00315 / 00316) pour un copilote incomplet ou invalide. */
export const COPILOT_INCOMPLETE_ERROR = 'copilot_incomplete';

/** Message levé par le serveur (00315) pour une date de naissance ou un email invalide. */
export const SUBMISSION_VALUE_INVALID_ERROR = 'submission_value_invalid';

/** Date de naissance la plus ancienne acceptée (identique à 00315). */
export const MIN_BIRTH_DATE = '1900-01-01';

/**
 * « Blancs » : propriété Unicode White_Space (U+0009-U+000D, U+0020, U+0085,
 * U+00A0, U+1680, U+2000-U+200A, U+2028, U+2029, U+202F, U+205F, U+3000) et
 * caractères invisibles de largeur nulle (U+180E, U+200B-U+200D, U+2060,
 * U+FEFF). Liste explicite identique à `form_value_is_blank` (00315) :
 * `String.prototype.trim()` ne retire pas U+0085 et le serveur ne dépend
 * d'aucune locale.
 */
const BLANK_CLASS =
  '\\u0009-\\u000D\\u0020\\u0085\\u00A0\\u1680\\u180E\\u2000-\\u200D\\u2028\\u2029\\u202F\\u205F\\u2060\\u3000\\uFEFF';
const BLANK_RE = new RegExp(`^[${BLANK_CLASS}]*$`);
const PERIPHERAL_BLANKS_RE = new RegExp(`^[${BLANK_CLASS}]+|[${BLANK_CLASS}]+$`, 'g');
const EMAIL_RE = new RegExp(`^[^@${BLANK_CLASS}]+@[^@${BLANK_CLASS}]+\\.[^@${BLANK_CLASS}]+$`);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Champs exclus du « miroir pilote » : ils dépendent du statut étudiant (école,
 * ville de l'école, profession — règle étudiant/profession existante) ou sont
 * rendus en case à cocher par la page publique (statut étudiant, newsletter).
 * Liste identique côté SQL (00315).
 */
const MIRROR_EXCLUDED_MAPS = new Set([
  'school',
  'school_city',
  'socio_professional_category',
  'is_not_student',
  'newsletter_opt_in',
]);

type ProductLike = { code?: string | null; name?: string | null };

/** Ce que les règles lisent d'un formulaire (un `Form` complet convient). */
type GuardedFormLike = Pick<Form, 'form_type' | 'generates_bpi'> & {
  product?: ProductLike | null;
};

/**
 * Édition du 4L Trophy : `products.code` commençant par « 4lt » (casse et
 * espaces autour ignorés) ou, sans code, nom commençant par « 4L Trophy ». Même
 * prédicat que `copilot_rule_applies` (00315) et que
 * `bed_product_has_age_limit` (lot DEROG-04). Rose Trip et les autres produits
 * ne sont pas concernés.
 */
export function isFourLProduct(product: ProductLike | null | undefined): boolean {
  if (!product) return false;
  // Comme btrim() côté SQL : seuls les espaces sont retirés autour du code.
  const code = (product.code ?? '').replace(/^ +| +$/g, '');
  if (code) return code.toLowerCase().startsWith('4lt');
  return /^4l trophy/i.test(product.name ?? '');
}

/**
 * Formulaire soumis à ces règles : duo de pré-inscription (génère un BPI)
 * d'une édition du 4L Trophy, même périmètre que le serveur
 * (`copilot_rule_applies`, 00315). Les autres formulaires ne changent pas.
 */
export function isGuardedForm(form: GuardedFormLike | null | undefined): boolean {
  return (
    !!form &&
    form.form_type === 'duo' &&
    form.generates_bpi === true &&
    isFourLProduct(form.product)
  );
}

/**
 * La case n'existe que sur un formulaire soumis aux règles qui a des champs
 * copilote.
 */
export function hasCopilotUnknownOption(
  form: GuardedFormLike | null | undefined,
  fields: FormFieldWithValues[]
): boolean {
  return isGuardedForm(form) && fields.some((f) => f.target_role === 'copilot');
}

function isCopilotFieldRequired(
  field: FormFieldWithValues,
  fields: FormFieldWithValues[]
): boolean {
  if (field.target_role !== 'copilot') return false;
  if (field.field_type === 'checkbox' || MIRROR_EXCLUDED_MAPS.has(field.contact_field_map ?? '')) {
    return false;
  }
  if (field.is_required) return true;
  if (!field.contact_field_map) return false;
  return fields.some(
    (f) =>
      f.target_role === 'pilot' && f.contact_field_map === field.contact_field_map && f.is_required
  );
}

/**
 * Champs copilote obligatoires quand un copilote est renseigné : requis par
 * eux-mêmes, ou dont le champ pilote de même `contact_field_map` est requis.
 */
export function getRequiredCopilotFields(fields: FormFieldWithValues[]): FormFieldWithValues[] {
  return fields.filter((f) => isCopilotFieldRequired(f, fields));
}

/**
 * Champs à valider selon l'état de la case :
 * - cochée : les champs copilote sont retirés (masqués, non validés, et absents
 *   des données envoyées puisque le schéma Zod ne garde que ses clés) ;
 * - décochée : les champs copilote en miroir du pilote deviennent obligatoires.
 */
export function applyCopilotRules(
  fields: FormFieldWithValues[],
  copilotUnknown: boolean
): FormFieldWithValues[] {
  if (copilotUnknown) {
    return fields.filter((f) => f.target_role !== 'copilot');
  }
  return fields.map((f) =>
    !f.is_required && isCopilotFieldRequired(f, fields) ? { ...f, is_required: true } : f
  );
}

/**
 * Vrai si la valeur est absente ou faite uniquement de blancs. Une valeur non
 * texte est lue comme son texte JSON (`false`, `0`, `[]` ne sont pas vides),
 * comme `data ->> champ` côté SQL pour les scalaires (pour un tableau ou un
 * objet, `->>` ajoute des espaces que `JSON.stringify` n'ajoute pas ; la page
 * n'envoie jamais ce type de valeur pour ces champs).
 */
export function isBlankValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return BLANK_RE.test(text);
}

/**
 * Retire les blancs périphériques (même liste que `isBlankValue`). Sert à
 * normaliser les emails collés (espaces insécables) avant validation et avant
 * envoi, comme `form_trim_blanks` côté serveur.
 */
export function trimBlanks(value: string): string {
  return value.replace(PERIPHERAL_BLANKS_RE, '');
}

/** Format d'email minimal : local@domaine.ext, sans blanc ni second @. */
export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

/**
 * Date de naissance AAAA-MM-JJ réelle, entre le 01/01/1900 et `today`
 * (AAAA-MM-JJ, date locale du navigateur). Le serveur tolère un jour de plus
 * (fuseaux horaires) : il n'est jamais plus strict que la page.
 */
export function isValidBirthDate(value: string, today: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return isRealDate && value >= MIN_BIRTH_DATE && value <= today;
}

/** Date du jour (AAAA-MM-JJ) dans le fuseau du navigateur. */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Champs copilote obligatoires laissés vides (valeur faite de blancs = vide,
 * comme côté serveur).
 */
export function findMissingCopilotFields(
  data: Record<string, unknown>,
  fields: FormFieldWithValues[]
): FormFieldWithValues[] {
  return getRequiredCopilotFields(fields).filter((f) => isBlankValue(data[f.id]));
}

/**
 * Message d'erreur d'une date de naissance ou d'un email RENSEIGNÉ mais
 * invalide (pilote et copilote : contrôles étendus aux deux, le pilote n'en
 * avait aucun côté serveur), sinon null.
 */
function valueIssue(field: FormFieldWithValues, value: unknown, today: string): string | null {
  if (isBlankValue(value)) return null;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (field.contact_field_map === 'birth_date' && !isValidBirthDate(text, today)) {
    return DATE_RE.test(text) && text > today
      ? `${field.label} : la date doit être passée`
      : `${field.label} : date invalide`;
  }
  if (field.contact_field_map === 'email' && !isValidEmail(trimBlanks(text))) {
    return `${field.label} : format d'email invalide`;
  }
  return null;
}

/** Dates de naissance et emails renseignés mais invalides, avec leur message. */
export function findInvalidValueFields(
  data: Record<string, unknown>,
  fields: FormFieldWithValues[],
  today: string
): Array<{ field: FormFieldWithValues; message: string }> {
  return fields.flatMap((field) => {
    const message = valueIssue(field, data[field.id], today);
    return message ? [{ field, message }] : [];
  });
}

/**
 * Ajoute au schéma Zod de la page, champ par champ, les contrôles de ces règles
 * (copilote obligatoire non vide, date de naissance, email normalisé) : toutes
 * les erreurs s'affichent au même clic que celles du schéma de base.
 * `validatedFields` = champs après `applyCopilotRules` (case cochée : plus
 * aucun champ copilote) ; `copilotUnknown` = état de la case.
 */
export function withCopilotChecks(
  schema: z.ZodObject<Record<string, z.ZodTypeAny>>,
  fields: FormFieldWithValues[],
  validatedFields: FormFieldWithValues[],
  copilotUnknown: boolean,
  today: string
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const requiredIds = new Set(
    copilotUnknown ? [] : getRequiredCopilotFields(fields).map((f) => f.id)
  );
  const extension: Record<string, z.ZodTypeAny> = {};
  for (const field of validatedFields) {
    const copilotRequired = requiredIds.has(field.id);
    const checksValue =
      field.contact_field_map === 'birth_date' || field.contact_field_map === 'email';
    const base = schema.shape[field.id];
    if (!base || (!copilotRequired && !checksValue)) continue;
    const checked = base.superRefine((value, ctx) => {
      const message =
        copilotRequired && isBlankValue(value)
          ? `${field.label} est requis`
          : valueIssue(field, value, today);
      if (message) ctx.addIssue({ code: 'custom', message });
    });
    // Emails : blancs périphériques retirés avant le contrôle de format ET dans
    // la valeur envoyée (le résolveur transmet la sortie du schéma).
    extension[field.id] =
      field.contact_field_map === 'email'
        ? z.preprocess((value) => (typeof value === 'string' ? trimBlanks(value) : value), checked)
        : checked;
  }
  // safeExtend : `extend` leve une erreur en Zod 4 si l'objet porte un jour un
  // raffinement au niveau objet (createDynamicFormSchema en a eu un).
  return schema.safeExtend(extension);
}

function hasServerMessage(error: unknown, code: string): boolean {
  if (typeof error !== 'object' || error === null || !('message' in error)) return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && message.includes(code);
}

/** Vrai si l'erreur Supabase/PostgREST est le refus « copilote incomplet ou invalide ». */
export function isCopilotIncompleteError(error: unknown): boolean {
  return hasServerMessage(error, COPILOT_INCOMPLETE_ERROR);
}

/** Vrai si l'erreur Supabase/PostgREST est le refus « date de naissance ou email invalide ». */
export function isSubmissionValueInvalidError(error: unknown): boolean {
  return hasServerMessage(error, SUBMISSION_VALUE_INVALID_ERROR);
}
